if (!customElements.get('continuous-image-slice-slider')) {
  customElements.define('continuous-image-slice-slider', class ContinuousImageSliceSlider extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.viewport = this.querySelector('[data-slice-viewport]');
      this.firstGroup = this.querySelector('[data-slice-group]');
      this.autoplay = this.dataset.autoplay !== 'false';
      this.direction = this.dataset.direction === 'right' ? -1 : 1;
      this.delay = Math.max(1000, Number(this.dataset.delay) || 3000);
      this.duration = Math.max(300, Number(this.dataset.transition) || 600);
      this.resumeDelay = Math.max(3000, Number(this.dataset.resumeDelay) || 8000);
      this.pauseHover = this.dataset.pauseHover === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pointerDown = false;
      this.isResetting = false;
      if (!this.viewport || !this.firstGroup) return;

      this.onPointerDown = () => {
        this.pointerDown = true;
        this.animating = false;
        cancelAnimationFrame(this.animationFrame);
        this.pause(this.resumeDelay);
      };
      this.onPointerUp = () => { this.pointerDown = false; this.normalizeLoop(); this.pause(this.resumeDelay); };
      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = null; this.normalizeLoop(); });
      };
      this.onMouseEnter = () => { if (this.pauseHover) this.stop(); };
      this.onMouseLeave = () => { if (this.pauseHover) this.schedule(); };
      this.onFocusIn = () => { if (this.pauseHover) this.stop(); };
      this.onFocusOut = (event) => { if (this.pauseHover && !this.contains(event.relatedTarget)) this.schedule(); };
      this.onVisibility = () => document.hidden ? this.stop() : this.schedule();
      this.onResize = () => { clearTimeout(this.resizeTimer); this.resizeTimer = setTimeout(() => this.normalizeLoop(true), 120); };

      this.viewport.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.viewport.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.viewport.addEventListener('pointercancel', this.onPointerUp, { passive: true });
      this.viewport.addEventListener('touchend', this.onPointerUp, { passive: true });
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibility);
      window.addEventListener('resize', this.onResize, { passive: true });

      if (this.direction < 0) requestAnimationFrame(() => this.jumpTo(this.groupWidth()));
      this.schedule();
    }

    disconnectedCallback() {
      this.stop();
      clearTimeout(this.resumeTimer);
      clearTimeout(this.resizeTimer);
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.animationFrame);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
      this.viewport?.removeEventListener('touchend', this.onPointerUp);
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('resize', this.onResize);
      this.ready = false;
    }

    stepSize() {
      const first = this.querySelector('[data-slice]');
      if (!first) return 0;
      const styles = getComputedStyle(this.firstGroup);
      return first.getBoundingClientRect().width + (Number.parseFloat(styles.columnGap || styles.gap) || 0);
    }

    groupWidth() { return this.firstGroup.getBoundingClientRect().width; }

    advance() {
      const step = this.stepSize();
      if (!step) return;
      this.animateTo(this.viewport.scrollLeft + step * this.direction);
    }

    animateTo(target) {
      cancelAnimationFrame(this.animationFrame);
      if (this.reducedMotion.matches) {
        this.jumpTo(target);
        this.normalizeLoop();
        return;
      }

      const start = this.viewport.scrollLeft;
      const distance = target - start;
      const startedAt = performance.now();
      this.animating = true;

      const tick = (now) => {
        if (!this.animating) return;
        const progress = Math.min(1, (now - startedAt) / this.duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.viewport.scrollLeft = start + distance * eased;
        if (progress < 1) {
          this.animationFrame = requestAnimationFrame(tick);
          return;
        }
        this.animating = false;
        this.normalizeLoop();
      };

      this.animationFrame = requestAnimationFrame(tick);
    }

    normalizeLoop(force = false) {
      if (this.isResetting || this.pointerDown || this.animating) return;
      const width = this.groupWidth();
      if (!width) return;
      const position = this.viewport.scrollLeft;
      let target = null;
      if (position >= width - 1) target = position - width;
      else if (this.direction < 0 && position <= 1) target = position + width;
      else if (force && position > width) target = position % width;
      if (target === null) return;
      this.isResetting = true;
      this.jumpTo(target);
      requestAnimationFrame(() => { this.isResetting = false; });
    }

    jumpTo(left) {
      const previous = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = left;
      this.viewport.style.scrollBehavior = previous;
    }

    pause(wait) {
      this.stop();
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => this.schedule(), wait);
    }

    schedule() {
      this.stop();
      if (!this.autoplay || this.reducedMotion.matches || document.hidden) return;
      this.timer = setTimeout(() => { this.advance(); this.schedule(); }, this.delay);
    }

    stop() { clearTimeout(this.timer); this.timer = null; }
  });
}
